/**
 * Județele României, ca **listă închisă**: cod scurt → denumire.
 *
 * Codul e cel pe care îl trimite ANAF în `scod_JudetAuto` (`PH`, `B`) — forma în care se
 * salvează județul firmei emitente (`crm_companies.county_code`,
 * [#183](https://github.com/bplugaru/efx-pcrm/issues/183)). XML-ul e-Factura cere codul
 * ISO 3166-2:RO (`RO-PH`); prefixul îl pune cine compune factura, nu lista.
 *
 * ⚠️ **Partajată, nu scrisă de două ori.** Formularul firmei alege din ea, serverul validează
 * după ea, iar emiterea traduce prin ea județul clienților, scris liber. Două liste s-ar fi
 * despărțit la prima corectură de diacritice.
 *
 * ⚠️ **Nu e lista modulului de facturare** (`RO_COUNTIES` din `@edin-inv/core`), deși are
 * aceleași 42 de intrări. Modulul nu cunoaște aplicația și aplicația nu-i dă liste: el
 * verifică codul ISO primit, gazda îl produce. Același preț plătit la porturi.
 */
export const RO_COUNTY_LIST = [
  { code: 'AB', name: 'Alba' },
  { code: 'AR', name: 'Arad' },
  { code: 'AG', name: 'Argeș' },
  { code: 'BC', name: 'Bacău' },
  { code: 'BH', name: 'Bihor' },
  { code: 'BN', name: 'Bistrița-Năsăud' },
  { code: 'BT', name: 'Botoșani' },
  { code: 'BR', name: 'Brăila' },
  { code: 'BV', name: 'Brașov' },
  { code: 'B', name: 'București' },
  { code: 'BZ', name: 'Buzău' },
  { code: 'CL', name: 'Călărași' },
  { code: 'CS', name: 'Caraș-Severin' },
  { code: 'CJ', name: 'Cluj' },
  { code: 'CT', name: 'Constanța' },
  { code: 'CV', name: 'Covasna' },
  { code: 'DB', name: 'Dâmbovița' },
  { code: 'DJ', name: 'Dolj' },
  { code: 'GL', name: 'Galați' },
  { code: 'GR', name: 'Giurgiu' },
  { code: 'GJ', name: 'Gorj' },
  { code: 'HR', name: 'Harghita' },
  { code: 'HD', name: 'Hunedoara' },
  { code: 'IL', name: 'Ialomița' },
  { code: 'IS', name: 'Iași' },
  { code: 'IF', name: 'Ilfov' },
  { code: 'MM', name: 'Maramureș' },
  { code: 'MH', name: 'Mehedinți' },
  { code: 'MS', name: 'Mureș' },
  { code: 'NT', name: 'Neamț' },
  { code: 'OT', name: 'Olt' },
  { code: 'PH', name: 'Prahova' },
  { code: 'SJ', name: 'Sălaj' },
  { code: 'SM', name: 'Satu Mare' },
  { code: 'SB', name: 'Sibiu' },
  { code: 'SV', name: 'Suceava' },
  { code: 'TR', name: 'Teleorman' },
  { code: 'TM', name: 'Timiș' },
  { code: 'TL', name: 'Tulcea' },
  { code: 'VL', name: 'Vâlcea' },
  { code: 'VS', name: 'Vaslui' },
  { code: 'VN', name: 'Vrancea' },
] as const;

export type RoCountyCode = (typeof RO_COUNTY_LIST)[number]['code'];

/** Codurile, pentru `z.enum`. */
export const RO_COUNTY_CODES = RO_COUNTY_LIST.map((county) => county.code) as [
  RoCountyCode,
  ...RoCountyCode[],
];

/** Bucureștiul: singurul „județ" la care localitatea e un sector. */
export const BUCHAREST_COUNTY_CODE = 'B' satisfies RoCountyCode;

const NAME_BY_CODE: ReadonlyMap<string, string> = new Map(
  RO_COUNTY_LIST.map((county) => [county.code, county.name]),
);

export function isRoCountyCode(value: string): value is RoCountyCode {
  return NAME_BY_CODE.has(value);
}

/** `CJ` → `Cluj`. Un cod necunoscut se întoarce neschimbat: se vede, nu dispare. */
export function countyName(code: string): string {
  return NAME_BY_CODE.get(code) ?? code;
}

/**
 * Litere mici, fără diacritice, fără punctuație: forma în care se compară textul liber.
 *
 * ⚠️ **Și sedila, nu doar virgula.** ANAF scrie „BUCUREŞTI" cu `Ş` (U+015E, sedilă), iar
 * tastatura românească dă `Ș` (U+0218, virgulă). Descompuse (NFD), amândouă lasă un semn
 * combinat peste `S`, pe care `\p{M}` îl scoate — altfel „Bucureşti" și „București" ar fi
 * fost două județe.
 */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Ce poate sta înaintea numelui într-un câmp de județ scris liber. */
const COUNTY_PREFIXES = /^(?:jud(?:etul)?|judet|municipiul|mun)\s+/;

const CODE_BY_FOLDED_NAME: ReadonlyMap<string, RoCountyCode> = new Map(
  RO_COUNTY_LIST.map((county) => [fold(county.name), county.code]),
);

/**
 * Județul scris liber → cod, sau `null` dacă nu se recunoaște **sigur**.
 *
 * Tolerează ce se găsește în fișele clienților și în răspunsurile ANAF: diacritice cu sau
 * fără, sedilă sau virgulă, majuscule, „jud.", „Județul", „Municipiul București", „Mun.
 * Bucuresti", codul scurt (`CJ`) și cel ISO (`RO-CJ`).
 *
 * ⚠️ **Nu ghicește.** „Cluj-Napoca" nu e un județ, iar „Satu" nu e „Satu Mare": textul trebuie
 * să fie numele întreg, după scoaterea prefixului. Un județ nerecunoscut devine motivul
 * scris pe rând, la emitere — o factură cu județul ghicit pleacă la ANAF cu adresa altcuiva.
 */
export function countyCodeFromText(value: string | null | undefined): RoCountyCode | null {
  if (value === null || value === undefined) {
    return null;
  }

  const raw = value.trim().toUpperCase();
  const code = raw.startsWith('RO-') ? raw.slice(3) : raw;

  if (isRoCountyCode(code)) {
    return code;
  }

  let folded = fold(value);

  // „Județul Cluj", „jud. Cluj", „Municipiul București" — o singură dată, nu în buclă: „mun
  // mun cluj" nu e o formă de adresă, e o greșeală de tastare care merită să se vadă.
  folded = folded.replace(COUNTY_PREFIXES, '');

  return CODE_BY_FOLDED_NAME.get(folded) ?? null;
}

/**
 * Sectorul din localitatea unei adrese din București, sau `null`.
 *
 * Recunoaște ce scrie ANAF („Sector 2 Mun. Bucureşti") și ce scrie un om („Sectorul 3",
 * „București, sector 4", `SECTOR5`). e-Factura cere sectorul în locul localității
 * (BR-RO-100), deci fără el o factură din București nu poate pleca.
 */
export function bucharestSector(city: string | null | undefined): number | null {
  if (city === null || city === undefined) {
    return null;
  }

  const match = /\bsector(?:ul)?\s*([1-6])\b/.exec(fold(city));

  return match ? Number(match[1]) : null;
}
